import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';

const AddEditEmailTemplateVariableModal = ({ isOpen, onClose, variable, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'string',
    defaultValue: '',
    required: false,
    category: 'user'
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      if (variable) {
        setFormData({
          name: variable.name || '',
          description: variable.description || '',
          type: variable.type || 'string',
          defaultValue: variable.defaultValue || '',
          required: variable.required !== undefined ? variable.required : false,
          category: variable.category || 'user'
        });
      } else {
        setFormData({
          name: '',
          description: '',
          type: 'string',
          defaultValue: '',
          required: false,
          category: 'user'
        });
      }
      setErrors({});
    }
  }, [isOpen, variable]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Variable name is required';
    } else if (!/^[a-z][a-z0-9_]*$/.test(formData.name.trim())) {
      newErrors.name = 'Variable name must start with a letter and contain only lowercase letters, numbers, and underscores';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.type) {
      newErrors.type = 'Variable type is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const variableData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        type: formData.type,
        defaultValue: formData.defaultValue.trim(),
        required: formData.required,
        category: formData.category,
        usageCount: variable?.usageCount || 0
      };

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      onSave(variableData);
      onClose();
    } catch (err) {
      setErrors({ submit: 'An error occurred while saving the variable' });
      console.error('Error saving variable:', err);
    } finally {
      setLoading(false);
    }
  };

  const typeOptions = [
    { value: 'string', label: 'String' },
    { value: 'email', label: 'Email' },
    { value: 'url', label: 'URL' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'datetime', label: 'Date & Time' },
    { value: 'boolean', label: 'Boolean' },
    { value: 'text', label: 'Text' }
  ];

  const categoryOptions = [
    { value: 'user', label: 'User' },
    { value: 'ticket', label: 'Ticket' },
    { value: 'agent', label: 'Agent' },
    { value: 'company', label: 'Company' },
    { value: 'system', label: 'System' },
    { value: 'feature', label: 'Feature' },
    { value: 'date', label: 'Date' },
    { value: 'sla', label: 'SLA' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {variable ? 'Edit Variable' : 'Add Variable'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Variable Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Variable Name *
            </label>
            <Input
              value={formData.name}
              onChange={(value) => setFormData({ ...formData, name: value })}
              placeholder="e.g., user_name, ticket_id, company_name"
              required
              className={errors.name ? 'border-red-500' : ''}
              disabled={!!variable}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Format: lowercase letters, numbers, and underscores only (e.g., user_name)
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description *
            </label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what this variable represents"
              rows={3}
              required
              className={errors.description ? 'border-red-500' : ''}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description}</p>
            )}
          </div>

          {/* Type and Category */}
          <div className="grid grid-cols-2 gap-4">
            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Type *
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.type ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                required
              >
                {typeOptions.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              {errors.type && (
                <p className="mt-1 text-sm text-red-600">{errors.type}</p>
              )}
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Category *
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.category ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                required
              >
                {categoryOptions.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
              {errors.category && (
                <p className="mt-1 text-sm text-red-600">{errors.category}</p>
              )}
            </div>
          </div>

          {/* Default Value */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Default Value
            </label>
            <Input
              value={formData.defaultValue}
              onChange={(value) => setFormData({ ...formData, defaultValue: value })}
              placeholder="Optional default value for this variable"
              className=""
            />
            <p className="mt-1 text-xs text-gray-500">
              This value will be used if the variable is not provided
            </p>
          </div>

          {/* Required */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="required"
              checked={formData.required}
              onChange={(e) => setFormData({ ...formData, required: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="required" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              Variable is required
            </label>
          </div>

          {/* Preview */}
          <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Variable Usage:</p>
            <code className="text-sm font-mono text-blue-600 dark:text-blue-400">
              {`{{${formData.name || 'variable_name'}}}`}
            </code>
          </div>

          {/* Error Message */}
          {errors.submit && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{errors.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? 'Saving...' : variable ? 'Update Variable' : 'Create Variable'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditEmailTemplateVariableModal;

